import type { AxiosResponse } from 'axios'

import { FAPI_CART_ENDPOINT } from '@/libs/constants/b2bApiEndpoints'
import environment from '@/environments/environment.dev'

import HttpClient from './apiHttpClient'

interface SessionParameter {
  sessionId?: string
}

type ReorderParameters = {
  orderNumber: string
} & SessionParameter

type AddProductParameters = {
  materialNumber: string
  quantity?: number
  productReference?: string
  voucherId?: string
} & SessionParameter

type UpdateProductParameters = {
  materialNumber: string
  quantity: number
  productReference?: string
  voucherId?: string
} & SessionParameter

type ReplaceProductParameters = {
  oldMaterialNumber: string
  replacedMaterialNumber: string
  quantity: number
  productReference?: string
  voucherId?: string
} & SessionParameter

interface RemoveProductParameters {
  materialNumber: string
}

type UploadProductsParameters = {
  formData: FormData
} & SessionParameter

type RedeemVoucherParameters = {
  voucherCode: string
} & SessionParameter

class CartService {
  private readonly httpClient: HttpClient
  private readonly baseEndpoint: string

  constructor(baseUrl: string) {
    this.httpClient = new HttpClient(baseUrl)
    this.baseEndpoint = FAPI_CART_ENDPOINT
  }

  /**
   * Helper method to append session ID to URL if provided
   * @param url Base URL
   * @param sessionId Optional session ID
   * @returns URL with session ID appended if provided
   */
  private appendSessionId(url: string, sessionId?: string): string {
    if (!sessionId) {
      return url
    }

    const searchParameters = new URLSearchParams()
    searchParameters.append('sessionId', sessionId)
    return `${url}?${searchParameters.toString()}`
  }

  /**
   * Get mini cart data
   */
  public async getMiniCart({ sessionId }: SessionParameter): Promise<AxiosResponse> {
    const url = this.appendSessionId(this.baseEndpoint, sessionId)
    return this.httpClient.get(url)
  }

  /**
   * Empty the cart
   */
  public async emptyCart({ sessionId }: SessionParameter): Promise<AxiosResponse> {
    const url = this.appendSessionId(`${this.baseEndpoint}/empty`, sessionId)
    return this.httpClient.delete(url)
  }

  /**
   * Reorder items from a previous order
   */
  public async reorder({ orderNumber, sessionId }: ReorderParameters): Promise<AxiosResponse> {
    const url = this.appendSessionId(`${this.baseEndpoint}/add/${orderNumber}`, sessionId)
    return this.httpClient.get(url)
  }

  /**
   * Add a product to the cart
   */
  public async addProductToCart({
    materialNumber,
    quantity = 1,
    productReference,
    voucherId,
    sessionId,
  }: AddProductParameters): Promise<AxiosResponse> {
    const url = `${this.baseEndpoint}/item/add`

    return this.httpClient.post(url, {
      body: {
        materialNumber,
        quantity,
        productReference,
        voucherId,
        sessionId,
      },
    })
  }

  /**
   * Upload products to cart via file upload
   */
  public async uploadProductsToCart(
    { formData, sessionId }: UploadProductsParameters,
  ): Promise<AxiosResponse> {
    const url = `${this.baseEndpoint}/items/add`

    return this.httpClient.post(url, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      body: formData,
      params: {
        sessionId,
      },
    })
  }

  /**
   * Update product quantity in cart
   */
  public async updateProductInCart({
    materialNumber,
    quantity,
    productReference,
    sessionId,
  }: UpdateProductParameters): Promise<AxiosResponse> {
    const url = `${this.baseEndpoint}/item/${materialNumber}/update`

    return this.httpClient.post(url, {
      body: {
        materialNumber,
        quantity,
        productReference,
        sessionId,
      },
    })
  }

  /**
   * Replace a product in the cart with another product
   */
  public async replaceProductInCart({
    oldMaterialNumber,
    replacedMaterialNumber,
    quantity,
    productReference,
    voucherId,
    sessionId,
  }: ReplaceProductParameters): Promise<AxiosResponse> {
    const url = `${this.baseEndpoint}/item/${oldMaterialNumber}/replace`

    return this.httpClient.put(url, {
      body: {
        materialNumber: replacedMaterialNumber,
        quantity,
        productReference,
        voucherId,
        sessionId,
      },
    })
  }

  /**
   * Remove a product from the cart
   */
  public async removeProductFromCart(
    { materialNumber }: RemoveProductParameters,
  ): Promise<AxiosResponse> {
    const url = `${this.baseEndpoint}/item/${materialNumber}/remove`
    return this.httpClient.delete(url)
  }

  /**
   * Remove a voucher from the cart
   */
  public async removeVoucher({ sessionId }: SessionParameter): Promise<AxiosResponse> {
    const url = this.appendSessionId(`${this.baseEndpoint}/voucher/remove`, sessionId)
    return this.httpClient.delete(url)
  }

  /**
   * Redeem a voucher code
   */
  public async redeemVoucher(
    { voucherCode, sessionId }: RedeemVoucherParameters,
  ): Promise<AxiosResponse> {
    const url = this.appendSessionId(`${this.baseEndpoint}/voucher/redeem`, sessionId)
    return this.httpClient.put(url, { body: { voucherCode } })
  }
}

// Create and export a singleton instance
export const cartService = new CartService(environment.shopFAPIUrl)

// Also export the class for testing or custom instances
export default CartService
